'use client'

import type { ChangeEventHandler, KeyboardEventHandler } from 'react'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ArrowLongRightIcon, ArrowLongDownIcon } from '@heroicons/react/24/solid'
import { useCopyToClipboard } from 'usehooks-ts'
import translate, { Race, Action, MAX_LENGTH } from '~/utils/aionTranslator'
import SelectBox from '~/components/SelectBox'
import Button from '~/components/Button'
import { cn } from '~/utils/cn'

const Translator: FC = () => {
	const [
		originRace,
		setOriginRace,
	] = useState<Race>(Race.ELYOS)
	const [
		action,
		setAction,
	] = useState<Action>(Action.SPEAK_TO)
	const [
		destRace,
		setDestRace,
	] = useState<Race>(Race.ASMO)
	const [
		input,
		setInput,
	] = useState<string>('')
	const [
		output,
		setOutput,
	] = useState<string>('')
	const [
		outputLanguage,
		setOutputLanguage,
	] = useState<Race>(Race.ASMO)
	const [
		copied,
		setCopied,
	] = useState<boolean>(false)
	const { enqueueSnackbar, closeSnackbar } = useSnackbar()

	const [
		, copy,
	] = useCopyToClipboard()

	const handleCopy = async () => {
		await copy(output)
		setCopied(true)
	}

	useEffect(() => {
		const handler = () => {
			setCopied(false)
		}

		const timeout = setTimeout(handler, 3000)

		return () => {
			clearTimeout(timeout)
		}
	}, [
		copied,
	])

	useEffect(() => {
		if (copied) {
			const snackbarKey = enqueueSnackbar('Copied', {
				persist: true,
				autoHideDuration: 3000,
				variant: 'success',
			})

			return () => {
				closeSnackbar(snackbarKey)
			}
		}
	}, [
		closeSnackbar,
		copied,
		enqueueSnackbar,
	])

	useEffect(() => {
		if (originRace === Race.ELYOS && destRace === Race.ASMO) {
			if (action === Action.SPEAK_TO) {
				setOutputLanguage(Race.ELYOS)
			} else {
				setOutputLanguage(Race.ASMO)
			}
		} else if (originRace === Race.ASMO && destRace === Race.ELYOS) {
			if (action === Action.SPEAK_TO) {
				setOutputLanguage(Race.ASMO)
			} else {
				setOutputLanguage(Race.ELYOS)
			}
		}
	}, [
		originRace,
		action,
		destRace,
	])

	useEffect(() => {
		setOutput(translate(input, outputLanguage))
	}, [
		input,
		outputLanguage,
	])

	const handleOriginRaceChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
		setOriginRace(event.target.value as Race)
		if (event.target.value === Race.ASMO) {
			setDestRace(Race.ELYOS)
		} else {
			setDestRace(Race.ASMO)
		}
	}

	const handleActionChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
		setAction(event.target.value as Action)
	}

	const handleDestRaceChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
		setDestRace(event.target.value as Race)
		if (event.target.value === Race.ASMO) {
			setOriginRace(Race.ELYOS)
		} else {
			setOriginRace(Race.ASMO)
		}
	}

	const handleInputChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
		setInput(event.target.value)
	}

	const handleEnterKey: KeyboardEventHandler<HTMLTextAreaElement> = async (event) => {
		if (event.key === 'Enter') {
			event.preventDefault()
			await handleCopy()

			return false
		}
	}

	const isElyosOutput = outputLanguage === Race.ELYOS
	const isAsmoOutput = outputLanguage === Race.ASMO

	const inputClasses = cn(
		'w-full px-5 pb-8 pt-5 md:px-8 md:py-5 resize-none rounded-t-2xl md:rounded-none md:rounded-l-2xl text-white border-none focus:outline-hidden focus:ring-transparent focus:border-transparent placeholder-gray-400 relative',
		isElyosOutput && 'bg-green-900',
		isAsmoOutput && 'bg-blue-900',
	)

	const outputClasses = cn(
		'w-full px-5 py-8 md:px-8 md:py-5 resize-none rounded-b-2xl md:rounded-none md:rounded-r-2xl text-white border-none focus:outline-hidden focus:ring-transparent focus:border-transparent cursor-not-allowed select-none',
		isAsmoOutput && 'bg-green-900',
		isElyosOutput && 'bg-blue-900',
	)

	return (
		<div className="rounded-2xl p-5">
			<div className="text-2xl pb-5 text-center md:text-left space-y-2 leading-10">
				<span>I&lsquo;m an </span>
				<SelectBox
					name="originRace"
					onChange={handleOriginRaceChange}
					value={originRace}
				>
					<option value={Race.ASMO}>Asmodian</option>
					<option value={Race.ELYOS}>Elyos</option>
				</SelectBox>
				<span> and I want to </span>
				<SelectBox
					name="action"
					onChange={handleActionChange}
					value={action}
				>
					<option value={Action.SPEAK_TO}>speak to</option>
					<option value={Action.UNDERSTAND}>understand</option>
				</SelectBox>
				<span> an </span>
				<SelectBox
					name="destRace"
					onChange={handleDestRaceChange}
					value={destRace}
				>
					<option value={Race.ELYOS}>Elyos</option>
					<option value={Race.ASMO}>Asmodian</option>
				</SelectBox>
				<span>.</span>
			</div>
			<div className="grid grid-cols-1 md:grid-cols-2 relative">
				<ArrowLongRightIcon
					className={cn(
						'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden md:block fill-current text-white z-10 max-w-[3.23rem]',
						isElyosOutput && 'text-green-900',
						isAsmoOutput && 'text-blue-900',
					)}
				/>
				<ArrowLongDownIcon
					className={cn(
						'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 md:hidden fill-current z-10 max-h-[3.23rem]',
						isElyosOutput && 'text-green-900',
						isAsmoOutput && 'text-blue-900',
					)}
				/>

				{/*From*/}
				<span className={cn(
					'relative rounded-t-2xl md:rounded-none md:rounded-l-2xl',
					isElyosOutput && 'bg-green-900',
					isAsmoOutput && 'bg-blue-900',
				)}
				>
					<textarea
						rows={8}
						value={input}
						onChange={handleInputChange}
						onKeyDown={handleEnterKey}
						className={inputClasses}
						maxLength={MAX_LENGTH}
						placeholder="Type what you would like to translate here..."
						data-gramm_editor={false}
					/>
					<div className={cn(
						'absolute bottom-2 left-0 text-white px-5 pt-1 pb-2 text-xs grid-cols-3 grid w-full rounded-none md:rounded-bl-2xl',
						isElyosOutput && 'bg-green-900',
						isAsmoOutput && 'bg-blue-900',
					)}
					>
						<p className="col-span-2">
							Press
							{' '}
							<b>Enter</b>
							{' '}
							to copy the translation
						</p>
						<p className="text-right">
							{input.length}
							/
							{MAX_LENGTH}
						</p>
					</div>
				</span>

				{/*To*/}
				<span className={cn(
					'relative rounded-b-2xl md:rounded-none md:rounded-r-2xl',
					isAsmoOutput && 'bg-green-900',
					isElyosOutput && 'bg-blue-900',
				)}
				>
					<textarea
						rows={8}
						value={output}
						readOnly={true}
						className={outputClasses}
						data-gramm_editor={false}
					/>
					<div className={cn(
						'absolute bottom-2 left-0 text-white px-5 pt-1 pb-2 text-xs w-full rounded-b-2xl md:rounded-br-2xl',
						isAsmoOutput && 'bg-green-900',
						isElyosOutput && 'bg-blue-900',
					)}
					>
						<p>This area is not editable.</p>
					</div>
				</span>
			</div>
			<div className="py-5 w-full flex flex-col items-center">
				<Button onClick={handleCopy}>
					{copied ? 'Copied' : 'Copy Translation'}
				</Button>
			</div>
		</div>
	)
}

export default Translator
